import React, { useState, useMemo, useEffect } from 'react';
import { PlusCircle, Edit2, Trash2, Download, DollarSign, BarChart3, Paperclip } from 'lucide-react';

import { DateRangeFilter } from '../../components/ui/DateRangeFilter';
import { TransactionModal } from './TransactionModal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Pagination } from '../../components/ui/Pagination';
import { ActionMenu, ActionMenuItem } from '../../components/ui/ActionMenu';
import { Card } from '../../components/ui/Card';
import { AttachmentViewerModal } from '../../components/ui/AttachmentViewerModal';
import { formatCurrency, formatDateForDisplay, parseAttachmentUrls, exportToExcel } from '../../utils/helpers';

const ITEMS_PER_PAGE = 20;

export const Accounting = ({ transactions, accounts, onSaveTransaction, onDeleteTransaction, fileHandler }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [deletingTransaction, setDeletingTransaction] = useState(null);
    const [viewingAttachments, setViewingAttachments] = useState({isOpen: false, urls: []});
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const sortedTransactions = useMemo(() => [...transactions].sort((a,b) => new Date(b.date) - new Date(a.date)), [transactions]);

    const filteredTransactions = useMemo(() => {
        return sortedTransactions 
            .filter(t => {
                if (!startDate || !endDate) return true;
                const transDate = new Date(t.date);
                const start = new Date(startDate);
                start.setHours(0,0,0,0);
                const end = new Date(endDate);
                end.setHours(23,59,59,999);
                return transDate >= start && transDate <= end;
            });
    }, [sortedTransactions, startDate, endDate]);

    const paginatedTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredTransactions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredTransactions, currentPage]);

    // *** FIX: Only reset to page 1 when filters change, not on data refresh ***
    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate]);

    const handleSave = (transactionData, files, isEditing) => {
        onSaveTransaction(transactionData, files, isEditing);
        setIsModalOpen(false);
        setEditingTransaction(null);
    };

    const openEditModal = (transaction) => {
        setEditingTransaction(transaction);
        setIsModalOpen(true);
    };

    const openDeleteModal = (transaction) => {
        setDeletingTransaction(transaction);
    };

    const handleDeleteConfirm = () => {
        onDeleteTransaction(deletingTransaction.id);
        setDeletingTransaction(null);
    };

    const totalIncome = useMemo(() => filteredTransactions.filter(t => t.type === 'Thu').reduce((sum, t) => sum + t.amount, 0), [filteredTransactions]);
    const totalExpense = useMemo(() => filteredTransactions.filter(t => t.type === 'Chi').reduce((sum, t) => sum + t.amount, 0), [filteredTransactions]);

    const handleExport = () => {
        const dataToExport = filteredTransactions.map(t => {
            const attachments = parseAttachmentUrls(t.attachmentUrl);
            return {
                'Mã Phiếu': t.id,
                'Ngày': formatDateForDisplay(t.date),
                'Loại': t.type,
                'Hạng Mục': t.category,
                'Số Tiền': t.amount,
                'Tài Khoản': accounts.find(a => a.id === t.accountId)?.name || t.accountId,
                'Đối Tượng': t.subject,
                'Ghi Chú': t.description,
                'Đính Kèm': attachments.join(', '),
            };
        });
        exportToExcel(dataToExport, 'SoThuChi');
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Sổ Thu Chi</h2>
                <div className="flex gap-2">
                    <button onClick={handleExport} className="flex items-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-green-700 transition">
                        <Download size={20} /> Xuất Excel
                    </button>
                     <button onClick={() => { setEditingTransaction(null); setIsModalOpen(true); }} className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition">
                        <PlusCircle size={20} /> Tạo Phiếu Mới
                    </button>
                </div>
            </div>
            
             <DateRangeFilter startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card title="Tổng Thu" value={formatCurrency(totalIncome)} icon={<div className="text-green-600"><DollarSign/></div>} />
                <Card title="Tổng Chi" value={formatCurrency(totalExpense)} icon={<div className="text-red-600"><DollarSign/></div>} />
                <Card title="Chênh lệch" value={formatCurrency(totalIncome - totalExpense)} icon={<BarChart3/>} />
            </div>

            {viewingAttachments.isOpen && (
                <AttachmentViewerModal 
                    urls={viewingAttachments.urls} 
                    onClose={() => setViewingAttachments({isOpen: false, urls: []})} 
                    onOpenFile={fileHandler.open} 
                />
             )}
            <TransactionModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSave={handleSave} 
                existingTransaction={editingTransaction}
                accounts={accounts}
            />
            <ConfirmationModal 
                isOpen={!!deletingTransaction} 
                onClose={() => setDeletingTransaction(null)} 
                onConfirm={handleDeleteConfirm} 
                title="Xác nhận Xóa Phiếu"
                message={`Bạn có chắc muốn xóa phiếu ${deletingTransaction?.id} (${formatCurrency(deletingTransaction?.amount)})? Hành động này không thể hoàn tác.`} 
            />

            <div className="bg-white rounded-lg shadow-md">
                <div className="overflow-x-auto p-6">
                       <table className="w-full text-left">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    {['Ngày', 'Loại', 'Hạng mục', 'Số tiền', 'Tài khoản', 'Đối tượng', 'Ghi chú', 'Đính kèm', 'Hành động'].map(h => <th key={h} className="p-4 font-semibold text-gray-600">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedTransactions.map(t => {
                                    const attachments = parseAttachmentUrls(t.attachmentUrl);
                                    
                                    return (
                                    <tr key={t.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">{formatDateForDisplay(t.date)}</td>
                                        <td className={`p-4 font-medium ${t.type === 'Thu' ? 'text-green-600' : 'text-red-600'}`}>{t.type}</td>
                                        <td className="p-4">{t.category}</td>
                                        <td className={`p-4 font-semibold text-right ${t.type === 'Thu' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(t.amount)}</td>
                                        <td className="p-4">{accounts.find(a => a.id === t.accountId)?.name}</td>
                                        <td className="p-4">{t.subject}</td>
                                        <td className="p-4 truncate max-w-xs">{t.description}</td>
                                        <td className="p-4 text-center">
                                            {attachments.length > 0 && (
                                                <button onClick={() => setViewingAttachments({isOpen: true, urls: attachments})} className="flex items-center gap-1.5 text-blue-600 hover:underline">
                                                    <Paperclip size={16} />
                                                    ({attachments.length})
                                                </button>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <ActionMenu>
                                                <ActionMenuItem icon={<Edit2 size={16} />} label="Sửa phiếu" onClick={() => openEditModal(t)} />
                                                <ActionMenuItem icon={<Trash2 size={16} />} label="Xóa phiếu" onClick={() => openDeleteModal(t)} />
                                            </ActionMenu>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                </div>
                 <Pagination
                    currentPage={currentPage}
                    totalItems={filteredTransactions.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    )
};
